import { useState } from 'react'
import { useIntl } from 'react-intl'
import { Modal } from 'react-bootstrap'
import '../styles/InvestorUpdates.scss'
import { createPitchDeck } from '../core/_request'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import { Form, Formik } from 'formik'
import { CustomButton } from '../../widgets/components/UI/CustomButton'
import TextInput from '../../widgets/components/Input/TextInput'
import { FileUpload } from '../../widgets/components/FileUpload'
import * as Yup from 'yup'
import cross from '../../../../_metronic/assets/images/svg/investor-crm/crossIcon.svg'
import { useInvestorDatabase } from '../../investor-database/core/InvestorContext'
import { errorMessage } from '../../profile/core/_constants'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'

const PitchDeckModal = ({ getAllPitchDeck }: { getAllPitchDeck?: () => void }) => {
  const [loading, setLoading] = useState(false)
  const { formatMessage } = useIntl()
  const [modelStatus, setModelStatus] = useState<boolean>(false)
  const [documentName, setDocumentName] = useState<string>('')
  const { companyId, setShowBillingModal, currentUser } = useAuth()
  const { pitchDeckModalShow, setPitchDeckModalShow } = useInvestorDatabase()

  const initialValues = {
    title: '',
    fileId: '',
  }

  const pitchDeckSchema = Yup.object().shape({
    title: Yup.string()
      .required(formatMessage({ id: 'Title is required' }))
      .nullable(),
    fileId: Yup.string()
      .required(formatMessage({ id: 'PDF is required' }))
      .nullable(),
  })

  const onSubmit = async (values: any) => {
    if (companyId) {
      try {
        setLoading(true)
        const {
          data: { success, errors },
        } = await createPitchDeck(values, companyId)
        if (success) {
          setDocumentName('')
          if (getAllPitchDeck) {
            getAllPitchDeck()
          }
          setPitchDeckModalShow(false)
          toast.success(formatMessage({ id: 'Pitch Deck Created Successfully' }))
          setLoading(false)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.CreatePitchDeckSuccessful,
              action: 'normal',
              metadata: {
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                title: values?.title,
                file_id: values?.fileId,
              },
            },
          })
        } else {
          setLoading(false)
          errors.forEach((error: string) => {
            if (
              error === errorMessage.UPGRADE_BILLING_PLAN_LIMIT_EXCEEDED ||
              error === errorMessage.UPGRADE_BILLING_PLAN_FEATURE
            ) {
              setShowBillingModal(true)
              toast.error(formatMessage({ id: error }), { autoClose: false })
            } else {
              toast.error(formatMessage({ id: error }))
            }
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.CreatePitchDeckFailed,
              action: 'normal',
              metadata: {
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                title: values?.title,
                file_id: values?.fileId,
                error: errors[0],
              },
            },
          })
        }
      } catch (err) {
        setLoading(false)
        console.log(err)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.CreatePitchDeckFailed,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              title: values?.title,
              file_id: values?.fileId,
              error: err,
            },
          },
        })
      }
    }
  }

  const handleOpen = () => {
    setModelStatus(true)
  }
  const handleClose = () => {
    setModelStatus(false)
  }
  return (
    <div>
      <Modal
        show={pitchDeckModalShow}
        onHide={() => {
          setPitchDeckModalShow(false)
        }}
        aria-labelledby='contained-modal-title-vcenter'
        centered
      >
        <Modal.Header closeButton>
          <h2>{formatMessage({ id: 'Pitch Deck' })}</h2>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          validationSchema={pitchDeckSchema}
          onSubmit={onSubmit}
        >
          {(formik) => {
            return (
              <Form>
                <Modal.Body>
                  <div>
                    <FileUpload
                      fileSize={10485760}
                      maxFileNumber={1}
                      allowType={[
                        'application/pdf',
                        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                      ]}
                      metaData={{ module: 'document', isProtected: false, companyId: companyId }}
                      modalStatus={modelStatus}
                      handleClose={handleClose}
                      handleSuccess={(id: number, name: string) => {
                        setDocumentName(name?.split('/').pop() || '')
                        formik.setFieldValue('fileId', id)
                      }}
                      resourceType='all'
                    />
                    <TextInput
                      fieldType={'text'}
                      label={formatMessage({ id: 'Title' })}
                      fieldName={'title'}
                      formik={formik}
                      placeholder={formatMessage({ id: 'Enter Title' })}
                      toolTipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.PITCHDECK_MODAL.TITLE',
                      })}
                      margin='me-6'
                      isStarRequired={true}
                      isTooltipForModal
                    />
                    <div className='mx-2 d-flex flex-column'>
                      <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                        {formatMessage({ id: 'Upload Pitch Deck' })}*
                        <ToolTipUIForModal
                          tooltipText={formatMessage({
                            id: 'GLOBAL.TOOLTIP.PITCHDECK_MODAL.PDF',
                          })}
                        />
                      </label>

                      <div className='d-flex flex-column gap-5'>
                        <button
                          className='btn btn-light font-size-13 font-weight-400 w-50'
                          onClick={handleOpen}
                        >
                          {formatMessage({ id: 'Upload' })}
                        </button>
                        <div className='d-flex gap-4 align-items-center'>
                          {documentName ? <p className='mt-2'>{documentName}</p> : ''}
                          {documentName && documentName.length > 0 && (
                            <img
                              src={cross}
                              alt='cross icon'
                              className='me-3 cursor-pointer'
                              onClick={() => {
                                setDocumentName('')
                                formik.setFieldValue('fileId', '')
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer className='p-0'>
                  <button
                    type='button'
                    className='btn btn-light font-size-13 font-weight-400'
                    onClick={() => {
                      setPitchDeckModalShow(false)
                      setLoading(false)
                    }}
                  >
                    {formatMessage({ id: 'Cancel' })}
                  </button>
                  <CustomButton
                    isSubmitting={formik.isSubmitting}
                    isValid={formik.isValid}
                    buttonText={formatMessage({ id: 'Create Pitch Deck' })}
                    loading={loading}
                    widthLoading={2}
                    width={2}
                    margin={'me-5 mt-5'}
                  />
                </Modal.Footer>
              </Form>
            )
          }}
        </Formik>
      </Modal>
    </div>
  )
}

export default PitchDeckModal
